'use client';

import React, { useState } from 'react';
import { ExtendedArchiMateDiagram } from '@/lib/archimate/extended-types';
import {
  importFromTerraform,
  importFromAWS,
  importFromAzure,
  importFromGCP,
  exportInfrastructureReport,
} from '@/lib/archimate/infrastructure-utils';
import { Cloud, Upload, Download, FileCode, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

interface InfrastructurePanelProps {
  diagram: ExtendedArchiMateDiagram;
  onUpdate: (diagram: ExtendedArchiMateDiagram) => void;
  onClose: () => void;
}

export default function InfrastructurePanel({
  diagram,
  onUpdate,
  onClose,
}: InfrastructurePanelProps) {
  const [viewMode, setViewMode] = useState<'import' | 'status'>('import');
  const [selectedProvider, setSelectedProvider] = useState<'terraform' | 'aws' | 'azure' | 'gcp'>(
    'terraform'
  );
  const [importStatus, setImportStatus] = useState<{
    success: boolean;
    message: string;
    elementsAdded: number;
  } | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportStatus(null);

    try {
      const content = await file.text();

      let result;
      switch (selectedProvider) {
        case 'terraform':
          result = importFromTerraform(content, diagram);
          break;
        case 'aws':
          result = importFromAWS(JSON.stringify(JSON.parse(content)), diagram);
          break;
        case 'azure':
          result = importFromAzure(JSON.stringify(JSON.parse(content)), diagram);
          break;
        case 'gcp':
          result = importFromGCP(JSON.stringify(JSON.parse(content)), diagram);
          break;
        default:
          throw new Error('Unknown provider');
      }

      const elementsAdded = result.elements.length - diagram.elements.length;

      onUpdate(result);
      setImportStatus({
        success: true,
        message: `Successfully imported infrastructure from ${selectedProvider.toUpperCase()}`,
        elementsAdded,
      });
      setViewMode('status');
    } catch (error) {
      setImportStatus({
        success: false,
        message: `Failed to import: ${error instanceof Error ? error.message : 'Unknown error'}`,
        elementsAdded: 0,
      });
    } finally {
      setIsImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleExportReport = () => {
    const report = exportInfrastructureReport(diagram);
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-infrastructure-report.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getProviderInfo = (provider: string) => {
    switch (provider) {
      case 'terraform':
        return {
          name: 'Terraform',
          description: 'Import from Terraform configuration files (.tf)',
          fileTypes: '.tf,.tf.json',
          icon: '🏗️',
          color: 'text-purple-400 bg-purple-500/20 border-purple-500/20',
        };
      case 'aws':
        return {
          name: 'AWS',
          description: 'Import from AWS CloudFormation or resource export',
          fileTypes: '.json,.yaml,.yml',
          icon: '☁️',
          color: 'text-accent bg-accent-muted border-border',
        };
      case 'azure':
        return {
          name: 'Azure',
          description: 'Import from Azure ARM templates or resource export',
          fileTypes: '.json',
          icon: '☁️',
          color: 'text-blue-400 bg-blue-500/20 border-blue-500/20',
        };
      case 'gcp':
        return {
          name: 'Google Cloud',
          description: 'Import from GCP Deployment Manager or resource export',
          fileTypes: '.json,.yaml,.yml',
          icon: '☁️',
          color: 'text-green-400 bg-green-500/20 border-green-500/20',
        };
      default:
        return {
          name: 'Unknown',
          description: '',
          fileTypes: '*',
          icon: '❓',
          color: 'text-foreground-tertiary bg-cream/10 border-border',
        };
    }
  };

  const renderImport = () => {
    const providerInfo = getProviderInfo(selectedProvider);

    return (
      <div className="space-y-4">
        {/* Provider Selection */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">Select Provider</h3>
          <div className="grid grid-cols-2 gap-2">
            {(['terraform', 'aws', 'azure', 'gcp'] as const).map((provider) => {
              const info = getProviderInfo(provider);
              return (
                <button
                  key={provider}
                  onClick={() => setSelectedProvider(provider)}
                  className={`p-4 rounded-lg border transition-colors text-left ${
                    selectedProvider === provider
                      ? info.color
                      : 'bg-cream/5 border-border hover:bg-cream/10'
                  }`}
                >
                  <div className="text-2xl mb-2">{info.icon}</div>
                  <div
                    className={`font-medium text-sm ${
                      selectedProvider === provider ? '' : 'text-foreground'
                    }`}
                  >
                    {info.name}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Provider Info */}
        <div className={`p-4 rounded-lg border ${providerInfo.color}`}>
          <div className="flex items-start gap-3">
            <div className="text-2xl">{providerInfo.icon}</div>
            <div className="flex-1">
              <div className="font-medium mb-1">{providerInfo.name}</div>
              <div className="text-sm opacity-80">{providerInfo.description}</div>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">Upload Configuration</h3>
          <label
            className={`block p-8 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
              isImporting
                ? 'border-border bg-cream/5 cursor-not-allowed'
                : 'border-border/40 hover:border-accent hover:bg-accent-muted'
            }`}
          >
            <input
              type="file"
              accept={providerInfo.fileTypes}
              onChange={handleFileUpload}
              disabled={isImporting}
              className="hidden"
            />
            <Upload className="w-8 h-8 text-foreground-tertiary mx-auto mb-3" />
            {isImporting ? (
              <>
                <div className="font-medium text-foreground mb-1">Importing...</div>
                <div className="text-sm text-foreground-tertiary">Please wait while we process your file</div>
              </>
            ) : (
              <>
                <div className="font-medium text-foreground mb-1">
                  Click to upload {providerInfo.name} configuration
                </div>
                <div className="text-sm text-foreground-tertiary">
                  Supported formats: {providerInfo.fileTypes}
                </div>
              </>
            )}
          </label>
        </div>

        {/* Instructions */}
        <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <div className="flex items-start gap-3">
            <FileCode className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-blue-400 text-sm mb-2">How to Export</div>
              <div className="text-xs text-foreground-secondary space-y-1">
                {selectedProvider === 'terraform' && (
                  <>
                    <div>1. Run: <code className="px-1 py-0.5 bg-background/50 rounded">terraform show -json &gt; terraform.json</code></div>
                    <div>2. Upload the generated JSON file</div>
                  </>
                )}
                {selectedProvider === 'aws' && (
                  <>
                    <div>1. Export CloudFormation template or use AWS CLI</div>
                    <div>2. aws cloudformation get-template --stack-name [name]</div>
                    <div>3. Upload the template JSON</div>
                  </>
                )}
                {selectedProvider === 'azure' && (
                  <>
                    <div>1. Export ARM template from Azure Portal</div>
                    <div>2. Or use: az group export --name [name]</div>
                    <div>3. Upload the template JSON</div>
                  </>
                )}
                {selectedProvider === 'gcp' && (
                  <>
                    <div>1. Export Deployment Manager config</div>
                    <div>2. Or use: gcloud deployment-manager deployments describe [name]</div>
                    <div>3. Upload the configuration file</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Current Infrastructure Summary */}
        <div className="p-4 bg-cream/5 rounded-lg">
          <div className="text-sm font-medium text-foreground mb-2">Current Infrastructure</div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-2xl font-bold text-foreground">
                {diagram.elements.filter((el) => el.layer === 'technology').length}
              </div>
              <div className="text-xs text-foreground-tertiary">Tech Components</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">
                {
                  diagram.elements.filter(
                    (el) => el.type === 'node' || el.type === 'device' || el.type === 'system-software'
                  ).length
                }
              </div>
              <div className="text-xs text-foreground-tertiary">Infrastructure</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">
                {diagram.relationships.filter((rel) => rel.type === 'assignment').length}
              </div>
              <div className="text-xs text-foreground-tertiary">Deployments</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStatus = () => {
    if (!importStatus) return null;

    return (
      <div className="space-y-4">
        {/* Status Card */}
        <div
          className={`p-6 rounded-lg border ${
            importStatus.success
              ? 'bg-green-500/10 border-green-500/20'
              : 'bg-red-500/10 border-red-500/20'
          }`}
        >
          <div className="flex items-start gap-4">
            {importStatus.success ? (
              <CheckCircle2 className="w-8 h-8 text-green-400 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0" />
            )}
            <div className="flex-1">
              <div
                className={`font-medium text-lg mb-2 ${
                  importStatus.success ? 'text-green-400' : 'text-red-400'
                }`}
              >
                {importStatus.success ? 'Import Successful' : 'Import Failed'}
              </div>
              <div className="text-foreground-secondary mb-3">{importStatus.message}</div>
              {importStatus.success && importStatus.elementsAdded > 0 && (
                <div className="text-sm text-foreground-tertiary">
                  Added {importStatus.elementsAdded} new element
                  {importStatus.elementsAdded !== 1 ? 's' : ''} to the diagram
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Updated Infrastructure Summary */}
        {importStatus.success && (
          <div className="p-4 bg-cream/5 rounded-lg">
            <div className="text-sm font-medium text-foreground mb-3">Updated Infrastructure</div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-2xl font-bold text-foreground">
                  {diagram.elements.filter((el) => el.layer === 'technology').length}
                </div>
                <div className="text-xs text-foreground-tertiary">Tech Components</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">
                  {
                    diagram.elements.filter(
                      (el) =>
                        el.type === 'node' || el.type === 'device' || el.type === 'system-software'
                    ).length
                  }
                </div>
                <div className="text-xs text-foreground-tertiary">Infrastructure</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">
                  {diagram.relationships.filter((rel) => rel.type === 'assignment').length}
                </div>
                <div className="text-xs text-foreground-tertiary">Deployments</div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => {
              setViewMode('import');
              setImportStatus(null);
            }}
            className="flex-1 px-4 py-2 bg-accent-muted hover:bg-accent-muted border border-border text-accent rounded transition-colors"
          >
            Import More
          </button>
          <button
            onClick={handleExportReport}
            className="px-4 py-2 bg-cream/10 hover:bg-cream/20 text-foreground rounded transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export Report
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed top-0 right-0 h-full w-[600px] bg-background/95 backdrop-blur border-l border-border shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <Cloud className="w-6 h-6 text-accent" />
          <h2 className="text-xl font-bold text-foreground">Infrastructure Import</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-cream/10 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="infrastructure"
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 p-4 border-b border-border">
        <button
          onClick={() => setViewMode('import')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'import'
              ? 'bg-accent text-accent-foreground'
              : 'bg-cream/10 text-foreground hover:bg-cream/20'
          }`}
        >
          Import
        </button>
        {importStatus && (
          <button
            onClick={() => setViewMode('status')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'status'
                ? 'bg-accent text-accent-foreground'
                : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Status
          </button>
        )}
        <button
          onClick={handleExportReport}
          className="ml-auto p-2 hover:bg-cream/10 rounded-lg transition-colors"
          title="Export Infrastructure Report"
        >
          <Download className="w-4 h-4 text-foreground" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {viewMode === 'import' && renderImport()}
        {viewMode === 'status' && renderStatus()}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-border text-xs text-foreground-tertiary text-center">
        Import cloud infrastructure to automatically generate ArchiMate diagrams
      </div>
    </div>
  );
}
